import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { loadBusinessDraft } from '../../lib/onboarding/business-draft';

/**
 * Banner de configuração incompleta no shell consumer (PRD §5.2.1, T1.15).
 *
 * Quem abandona o onboarding profissional a meio fica com o negócio em `draft` — o estado que o
 * PRD chama `SETUP_INCOMPLETE`. O banner é o caminho de volta: sem ele, o negócio a meio ficaria
 * invisível e a pessoa teria de descobrir sozinha o URL do onboarding.
 *
 * 🔴 **Informa, não bloqueia** (RN-7). Toda conta tem base consumer (PRD §3.5): quem tem um
 * negócio por terminar continua a poder usar o app como consumidor. Prender o shell até concluir
 * um fluxo de 25–45 min transformaria um lembrete num castigo.
 *
 * Por isso é `role="status"` e não `role="alert"`: informação persistente, não urgência que
 * interrompe quem usa leitor de ecrã.
 */
export function SetupIncompleteBanner() {
  const { t } = useTranslation();
  const [temRascunho, setTemRascunho] = useState(false);
  const [dispensado, setDispensado] = useState(false);

  useEffect(() => {
    let vivo = true;

    // Falhar a consulta não pode mostrar um banner sem fundamento nem esconder o app: no erro,
    // simplesmente não aparece, e a próxima montagem tenta de novo.
    void loadBusinessDraft()
      .then((resultado) => {
        if (vivo && resultado.ok && resultado.data) setTemRascunho(true);
      })
      .catch(() => {
        /* silêncio deliberado — ver acima */
      });

    return () => {
      vivo = false;
    };
  }, []);

  if (!temRascunho || dispensado) return null;

  return (
    <div
      role="status"
      className="panel-quiet mx-4 mt-4 flex min-w-0 items-start gap-3 rounded-[var(--r-md)] p-4"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <p className="min-w-0 text-[length:var(--t-sm)] text-ink-1">
          {t('proOnboarding.setupBanner.title')}
        </p>

        <Link
          to="/dashboard/onboarding"
          className="inline-flex min-h-[44px] items-center text-[length:var(--t-sm)] text-gold-text underline underline-offset-4 hover:text-gold focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2"
        >
          {t('proOnboarding.setupBanner.cta')}
        </Link>
      </div>

      {/* Dispensar esconde o lembrete nesta sessão; não apaga o rascunho nem marca nada como
          feito — o negócio continua em `draft` e o banner volta na próxima abertura. */}
      <button
        type="button"
        onClick={() => setDispensado(true)}
        aria-label={t('actions.close')}
        className="hit shrink-0 text-ink-3 hover:text-ink-1"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
